import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useAnimal, useAnimalCoverUrls, useAnimalMedicalEvents, useAnimalTransactions,
  updateLeadStatus, updateAnimal, changeAnimalStatus, toast, useAuth,
  canonicalizeTraits, normalizeTrait, useTraitVocabulary, AddressAutocompleteInput,
  ageFromBirthDate, StaticMap, resolvePhotoUrls, formatHUF,
  vaccinationsForSpecies, HEARTWORM_LABELS, MEDICAL_EVENT_LABELS,
} from '@rehome/shared';
import type { Species, Animal, HeartwormResult } from '@rehome/shared';
import {
  ArrowLeft, Calendar, HeartHandshake, Loader2, CheckCircle2, XCircle, MessageSquare,
  Pencil, Save, X, Plus, Trash2, FileCheck, ChevronRight, ShieldOff, ImageIcon,
  Stethoscope, User, Wallet, Syringe, HelpCircle, MinusCircle, AlertTriangle, Camera,
} from 'lucide-react';
import { SpeciesBreedSelect } from '../components/SpeciesBreedSelect';
import { AnimalPhotoManager } from '../components/AnimalPhotoManager';
import { AnimalCheckInSection } from '../components/AnimalCheckInSection';
// Az aloldalak beágyazva jelennek meg a füleken. Nem másolat: ugyanaz a
// komponens, csak az oldal-keret nélkül — így nincs két példány ugyanabból
// az űrlapból, ami előbb-utóbb szétcsúszna.
import AnimalRequirementsPage from './AnimalRequirementsPage';
import AnimalIntakeDetailsPage from './AnimalIntakeDetailsPage';
import AnimalMedicalPage from './AnimalMedicalPage';
import { StatusChangeDialog } from '../components/StatusChangeDialog';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_FLOW: Record<string, string[]> = {
  New: ['Contacted', 'Rejected'],
  Contacted: ['Vetting', 'Rejected'],
  Vetting: ['Approved', 'Rejected'],
  Approved: [],
  Rejected: [],
};

const STATUS_LABELS: Record<string, string> = {
  New: 'Új', Contacted: 'Felvéve', Vetting: 'Ellenőrzés', Approved: 'Elfogadva', Rejected: 'Elutasítva',
};

const STATUS_COLORS: Record<string, string> = {
  New: 'bg-gray-100 text-gray-700', Contacted: 'bg-blue-100 text-blue-700', Vetting: 'bg-yellow-100 text-yellow-700', Approved: 'bg-green-100 text-green-700', Rejected: 'bg-red-100 text-red-700',
};

const LABELS: Record<string, string> = {
  Available: 'Gazdikereső', AdoptionPending: 'Örökbefogadás folyamatban', Adopted: 'Örökbefogadva', Foster: 'Ideiglenesben', Medical: 'Orvosi kezelés alatt', StrayHold: 'Várakozási idő (Stray)', Quarantine: 'Karantén',
};

const STYLES: Record<string, string> = {
  Available: 'bg-green-100 text-green-800', AdoptionPending: 'bg-amber-100 text-amber-800', Adopted: 'bg-blue-100 text-blue-800', Foster: 'bg-orange-100 text-orange-800', Medical: 'bg-red-100 text-red-800', StrayHold: 'bg-purple-100 text-purple-800', Quarantine: 'bg-yellow-100 text-yellow-800',
};

// Kept in sync with the intake wizard's own dropdown wording (IntakeFormPage.tsx step 1).
const INTAKE_LABELS: Record<string, string> = {
  Stray: 'Talált (Kóbor)', Surrender: 'Leadott (Tulajdonosi)', Confiscated: 'Elkobzott (Hatósági)', Return: 'Visszavett (Sikertelen örökbefogadás)',
};

/**
 * A fülek a design szerint. A designon szereplő, de a rendszerben nem létező
 * AI-funkciók (pl. "Örökbefogadási Esély" becslés) szándékosan kimaradtak —
 * egy sosem frissülő, kitalált szám rosszabb, mint a hiánya.
 */
const TABS = [
  { id: 'overview' as const, label: 'Áttekintés', icon: User },
  { id: 'health' as const, label: 'Egészségügy', icon: Stethoscope },
  { id: 'requirements' as const, label: 'Örökbefogadási feltételek', icon: FileCheck },
  { id: 'finance' as const, label: 'Pénzügyek', icon: Wallet },
  { id: 'leads' as const, label: 'Érdeklődők', icon: HeartHandshake },
];

type ProfileTab = (typeof TABS)[number]['id'];

const GENDERS = ['Kan', 'Nőstény'];
const INTAKE_TYPES = ['Stray', 'Surrender', 'Confiscated', 'Return'];

/** Tri-state (nem ismert / igen / nem) helper — the DB column is a nullable boolean. */
const triStateToValue = (v: boolean | null | undefined): string => (v === true ? 'yes' : v === false ? 'no' : '');
const valueToTriState = (v: string): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null);

// ─── Component ──────────────────────────────────────────────────────────────

const AnimalProfilePage: React.FC = () => {
  const { shelterId, animalId } = useParams<{ shelterId: string; animalId: string }>();
  const navigate = useNavigate();
  const { animal, loading, refetch } = useAnimal(animalId);
  const { userProfile } = useAuth();
  const [updatingLead, setUpdatingLead] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [tab, setTab] = useState<ProfileTab>('overview');

  const handleChangeStatus = async (newStatus: string, reason: string) => {
    if (!animalId) return;
    const result = await changeAnimalStatus(animalId, newStatus, reason);
    if (result.success) {
      setStatusDialogOpen(false);
      refetch();
    }
  };

  // Form state — initialized from animal data
  const [form, setForm] = useState({
    name: '',
    species: 'Kutya' as string,
    breed: '',
    // A kor a születési dátumból SZÁRMAZIK (l. resolveAge a mutations.ts-ben);
    // az `animals.age` csak visszafelé kompatibilis tartalék.
    birthDate: '' as string,
    gender: '' as string,
    weightKg: '' as string,
    traits: [] as string[],
    bio: '',
    intakeType: '' as string,
    intakeDate: '',
    microchipId: '',
    chipProvider: '',
    chipStatus: '',
    color: '',
    isVaccinated: '' as string,
    isNeutered: '' as string,
    isDewormed: '' as string,
    foundAddress: '',
    foundLat: '' as string,
    foundLng: '' as string,
  });
  const [newTrait, setNewTrait] = useState('');
  const [addingTrait, setAddingTrait] = useState(false);
  // A már használt kulcsszavak — javaslatként a beviteli mező alatt.
  const { vocabulary: traitVocabulary } = useTraitVocabulary();

  // A képek NEM az űrlap részei: hozzáadásuk, törlésük és a láthatóságuk
  // váltása azonnali, önmagában lezáruló művelet — l. AnimalPhotoManager.
  const photoCount = (animal?.gallery?.length || 0) + (animal?.privateGallery?.length || 0);
  const coverUrl = useAnimalCoverUrls(animal ? [animal] : [])[animal?.id || ''];
  // A születési dátum a pontosabb forrás; az `age` csak tartalék.
  const derivedAge = ageFromBirthDate(animal?.birthDate) ?? animal?.age ?? null;

  /** Blocks saving until the required identification fields are filled. */
  const validateRequiredFields = (): string | null => {
    if (!form.name.trim()) return 'Add meg az állat nevét.';
    if (!form.breed.trim()) return 'Add meg (vagy válaszd ki) a fajtát.';
    if (!form.gender) return 'Add meg az állat nemét.';
    if (photoCount === 0) return 'Legalább 1 fotó szükséges.';
    return null;
  };

  // Sync form with animal data when loaded or refetched
  useEffect(() => {
    if (animal) {
      setForm({
        name: animal.name || '',
        species: animal.species || 'Kutya',
        breed: animal.breed || '',
        birthDate: animal.birthDate || '',
        gender: animal.gender || '',
        weightKg: animal.weightKg?.toString() || '',
        traits: [...(animal.traits || [])],
        bio: animal.bio || '',
        intakeType: animal.intakeType || '',
        intakeDate: animal.intakeDate || '',
        microchipId: animal.microchipId || '',
        chipProvider: animal.chipProvider || '',
        chipStatus: animal.chipStatus || '',
        color: animal.color || '',
        isVaccinated: triStateToValue(animal.isVaccinated),
        isNeutered: triStateToValue(animal.isNeutered),
        isDewormed: triStateToValue(animal.isDewormed),
        foundAddress: animal.foundLocation?.address || '',
        foundLat: animal.foundLocation?.lat ? animal.foundLocation.lat.toString() : '',
        foundLng: animal.foundLocation?.lng ? animal.foundLocation.lng.toString() : '',
      });
    }
  }, [animal]);

  const handleLeadStatusChange = async (leadId: string, newStatus: string) => {
    setUpdatingLead(leadId);
    const ok = await updateLeadStatus(leadId, newStatus);
    if (ok) refetch();
    setUpdatingLead(null);
  };

  const handleSave = async () => {
    if (!animalId) return;
    const validationError = validateRequiredFields();
    if (validationError) { toast.error(validationError); return; }

    setSaving(true);

    const lat = form.foundLat ? parseFloat(form.foundLat) : null;
    const lng = form.foundLng ? parseFloat(form.foundLng) : null;
    await updateAnimal(animalId, {
      name: form.name,
      species: form.species,
      breed: form.breed || null,
      // Csak a dátumot küldjük: az `age`-et a mutáció vezeti le belőle, így a
      // kettő nem tud szétcsúszni. Dátum törlésekor a régi kor tartalékként marad.
      birthDate: form.birthDate || null,
      gender: form.gender || null,
      weightKg: form.weightKg ? parseFloat(form.weightKg) : null,
      traits: form.traits,
      bio: form.bio || null,
      intakeType: form.intakeType || null,
      intakeDate: form.intakeDate || undefined,
      microchipId: form.microchipId || null,
      chipProvider: form.chipProvider || null,
      chipStatus: form.chipStatus || null,
      color: form.color || null,
      isVaccinated: valueToTriState(form.isVaccinated),
      isNeutered: valueToTriState(form.isNeutered),
      isDewormed: valueToTriState(form.isDewormed),
      foundLocation: (form.foundAddress.trim() || (lat && lng)) ? {
        address: form.foundAddress.trim() || undefined,
        lat: lat ?? animal?.foundLocation?.lat ?? 0,
        lng: lng ?? animal?.foundLocation?.lng ?? 0,
        foundDate: animal?.foundLocation?.foundDate,
      } : null,
    });
    setSaving(false);
    setIsEditing(false);
    refetch();
  };

  const handleCancel = () => {
    if (animal) {
      setForm({
        name: animal.name || '',
        species: animal.species || 'Kutya',
        breed: animal.breed || '',
        birthDate: animal.birthDate || '',
        gender: animal.gender || '',
        weightKg: animal.weightKg?.toString() || '',
        traits: [...(animal.traits || [])],
        bio: animal.bio || '',
        intakeType: animal.intakeType || '',
        intakeDate: animal.intakeDate || '',
        microchipId: animal.microchipId || '',
        chipProvider: animal.chipProvider || '',
        chipStatus: animal.chipStatus || '',
        color: animal.color || '',
        isVaccinated: triStateToValue(animal.isVaccinated),
        isNeutered: triStateToValue(animal.isNeutered),
        isDewormed: triStateToValue(animal.isDewormed),
        foundAddress: animal.foundLocation?.address || '',
        foundLat: animal.foundLocation?.lat ? animal.foundLocation.lat.toString() : '',
        foundLng: animal.foundLocation?.lng ? animal.foundLocation.lng.toString() : '',
      });
      // A képeken nincs mit visszavonni: azok műveletei már lezárultak.
    }
    setIsEditing(false);
  };

  // A beírt szót a közös szótárhoz igazítjuk, mielőtt chipként megjelenne:
  // ha van már rokon értelmű kulcsszó ("kedves" -> "Barátságos"), azt vesszük
  // fel, így a platform szűrőjében nem lesz belőle külön chip. A staff rögtön
  // látja az eredményt, és ha nem tetszik, egy kattintással törli.
  const addTrait = async () => {
    const t = newTrait.trim();
    if (!t || addingTrait) return;

    setAddingTrait(true);
    const [canonical = t] = await canonicalizeTraits([t]);
    setAddingTrait(false);

    setNewTrait('');
    setForm(prev =>
      prev.traits.some(existing => normalizeTrait(existing) === normalizeTrait(canonical))
        ? prev
        : { ...prev, traits: [...prev.traits, canonical] }
    );
  };

  const removeTrait = (trait: string) => {
    setForm(prev => ({ ...prev, traits: prev.traits.filter(t => t !== trait) }));
  };

  // user_profiles.role values: 'user' | 'shelter_admin' | 'super_admin'
  // ('Admin'/'Staff' live in shelter_staff, not here). RLS enforces the
  // actual write permission — this only controls UI affordances.
  const canEdit = userProfile?.role === 'shelter_admin' || userProfile?.role === 'super_admin';

  // ─── Loading state ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-64 bg-gray-200 rounded-2xl" />
          <div className="h-40 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!animal) return <div className="p-8 text-center text-gray-500">Állat nem található.</div>;

  // ─── Shared field styling ─────────────────────────────────────────────
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white disabled:bg-gray-50 disabled:text-gray-500';
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Back + Edit button row */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('../animals')} className="flex items-center gap-2 text-gray-500 hover:text-teal-700 transition-colors">
          <ArrowLeft className="w-4 h-4" />Vissza a listához
        </button>
        {canEdit && !isEditing && (
          <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors">
            <Pencil className="w-4 h-4" />Szerkesztés
          </button>
        )}
        {isEditing && (
          <div className="flex items-center gap-2">
            <button onClick={handleCancel} className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors">
              <X className="w-4 h-4" />Mégsem
            </button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Mentés
            </button>
          </div>
        )}
      </div>

      {/* ─── Fejléc ──────────────────────────────────────────────────────
          A borítókép sávként fut, az arckép fölé lóg — így az állat arca
          akkor is felismerhető marad, ha a borítón épp csak egy részlet
          látszik. A design szerinti felépítés. */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="h-36 sm:h-44 relative bg-gray-800">
          {coverUrl
            ? <img src={coverUrl} className="w-full h-full object-cover" alt="" />
            : <div className="w-full h-full bg-gradient-to-br from-teal-800 to-gray-800" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>

        <div className="px-6 pb-0">
          {/* `relative` kell: a borítón lévő színátmenet ABSZOLÚT pozicionált,
              és a CSS festési sorrendje szerint a pozicionált elemek a nem
              pozicionáltak fölé kerülnek — DOM-sorrendtől függetlenül. E
              nélkül az arckép fölé lógó része eltűnne a borító alatt. */}
          <div className="relative flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-14">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden border-4 border-white bg-gray-100 shadow-sm flex-shrink-0">
              {coverUrl
                ? <img src={coverUrl} className="w-full h-full object-cover" alt={animal.name} />
                : <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon className="w-8 h-8" /></div>}
            </div>

            <div className="flex-1 min-w-0 pb-1">
              {isEditing ? (
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="text-3xl font-bold border-b-2 border-gray-200 outline-none focus:border-teal-500 w-full"
                  placeholder="Állat neve"
                />
              ) : (
                <h1 className="text-3xl font-bold text-gray-900 truncate">{animal.name}</h1>
              )}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-sm text-gray-500">
                <span>{animal.species}</span>
                {animal.breed && <><span className="text-gray-300">•</span><span>{animal.breed}</span></>}
                {derivedAge !== null && <><span className="text-gray-300">•</span><span>{derivedAge} éves</span></>}
                {animal.gender && <><span className="text-gray-300">•</span><span>{animal.gender}</span></>}
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => setStatusDialogOpen(true)}
                    title="Kattints a státusz módosításához"
                    className={`ml-1 px-2.5 py-0.5 rounded-full text-xs font-semibold hover:ring-2 hover:ring-offset-1 hover:ring-teal-400 transition-shadow ${STYLES[animal.status]}`}
                  >
                    {LABELS[animal.status] || animal.status}
                  </button>
                ) : (
                  <span className={`ml-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${STYLES[animal.status]}`}>{LABELS[animal.status] || animal.status}</span>
                )}
              </div>
            </div>

            {/* A chipszám az azonosítás legfontosabb adata — a design külön
                dobozban emeli ki, a fejléc jobb szélén. */}
            {animal.microchipId && (
              <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-2 text-right flex-shrink-0">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Chip szám</p>
                <p className="font-mono text-sm text-gray-800">{animal.microchipId}</p>
              </div>
            )}
          </div>

          {/* ── Fülek ──────────────────────────────────────────────────
              Szerkesztés közben elrejtve: az űrlap egyben tartozik össze,
              és fülek közt váltogatva a félig kitöltött mezők könnyen
              elvesznének a szem elől. */}
          {!isEditing && (
            <div className="flex gap-1 mt-5 -mx-1 overflow-x-auto" role="tablist">
              {TABS.map(t => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                    tab === t.id
                      ? 'border-teal-600 text-teal-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <t.icon className="w-4 h-4" />{t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {isEditing && (
          <div className="p-6 border-t border-gray-100 mt-5">
            <div className="space-y-6">
              {/* Fotók — a műveletek azonnal érvényesülnek, nem az űrlap
                  mentésével együtt (l. AnimalPhotoManager). */}
              <div>
                <label className={labelCls}><ImageIcon className="w-4 h-4 inline mr-1 -mt-0.5" />Fotók</label>
                <AnimalPhotoManager
                  animalId={animalId!}
                  gallery={animal.gallery || []}
                  privateGallery={animal.privateGallery || []}
                  onChanged={refetch}
                />
              </div>

              {/* Row 1: Species + Breed (cascading), Gender, Age */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SpeciesBreedSelect
                  species={form.species}
                  breed={form.breed}
                  onSpeciesChange={species => setForm(prev => ({ ...prev, species: species as Species }))}
                  onBreedChange={breed => setForm(prev => ({ ...prev, breed }))}
                  inputClassName={inputCls}
                  labelClassName={labelCls}
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Nem</label>
                    <select value={form.gender} onChange={e => setForm(prev => ({ ...prev, gender: e.target.value }))} className={inputCls}>
                      <option value="">—</option>
                      {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Születési dátum</label>
                    <input
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                      value={form.birthDate}
                      onChange={e => setForm(prev => ({ ...prev, birthDate: e.target.value }))}
                      className={inputCls}
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      {form.birthDate
                        ? `Kb. ${ageFromBirthDate(form.birthDate) ?? 0} éves.`
                        : animal?.age != null
                          ? `Nincs dátum — a rögzített kor ${animal.age} év.`
                          : 'Becsléshez elég a hozzávetőleges év (pl. január 1-jével).'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Row 2: Weight, Color — status changes go through the badge dialog (audit-logged), not this form */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Súly (kg)</label>
                  <input type="number" min="0" step="0.1" value={form.weightKg} onChange={e => setForm(prev => ({ ...prev, weightKg: e.target.value }))} className={inputCls} placeholder="pl. 15.5" />
                </div>
                <div>
                  <label className={labelCls}>Szín / külső jegyek</label>
                  <input type="text" value={form.color} onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))} className={inputCls} placeholder="pl. fekete-fehér" />
                </div>
              </div>

              {/* Row 2b: Health flags — public, standard shelter-listing info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Oltott</label>
                  <select value={form.isVaccinated} onChange={e => setForm(prev => ({ ...prev, isVaccinated: e.target.value }))} className={inputCls}>
                    <option value="">Nem ismert</option><option value="yes">Igen</option><option value="no">Nem</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Ivartalanítva</label>
                  <select value={form.isNeutered} onChange={e => setForm(prev => ({ ...prev, isNeutered: e.target.value }))} className={inputCls}>
                    <option value="">Nem ismert</option><option value="yes">Igen</option><option value="no">Nem</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Parazitamentes</label>
                  <select value={form.isDewormed} onChange={e => setForm(prev => ({ ...prev, isDewormed: e.target.value }))} className={inputCls}>
                    <option value="">Nem ismert</option><option value="yes">Igen</option><option value="no">Nem</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Intake info */}
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Bekerülés típusa</label>
                  <select value={form.intakeType} onChange={e => setForm(prev => ({ ...prev, intakeType: e.target.value }))} className={inputCls}>
                    <option value="">—</option>
                    {INTAKE_TYPES.map(t => <option key={t} value={t}>{INTAKE_LABELS[t] || t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Bekerülés dátuma</label>
                  <input type="date" value={form.intakeDate} onChange={e => setForm(prev => ({ ...prev, intakeDate: e.target.value }))} className={inputCls} />
                </div>
              </div>

              {/* Row 4: Microchip info (owner info moved to the staff-only Bekerülési részletek panel below) */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Chip szám</label>
                  <input type="text" value={form.microchipId} onChange={e => setForm(prev => ({ ...prev, microchipId: e.target.value }))} className={inputCls} placeholder="pl. 977200000000000" />
                </div>
                <div>
                  <label className={labelCls}>Chip szolgáltató</label>
                  <select value={form.chipProvider} onChange={e => setForm(prev => ({ ...prev, chipProvider: e.target.value }))} className={inputCls}>
                    <option value="">—</option>
                    <option value="24PetWatch">24PetWatch</option>
                    <option value="Petlog">Petlog</option>
                    <option value="MAVK">MAVK</option>
                    <option value="Unknown">Ismeretlen</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Chip státusz</label>
                  <select value={form.chipStatus} onChange={e => setForm(prev => ({ ...prev, chipStatus: e.target.value }))} className={inputCls}>
                    <option value="">—</option>
                    <option value="Registered">Regisztrált</option>
                    <option value="Unregistered">Nem regisztrált</option>
                    <option value="Lost_Reported">Eltűntként jelentett</option>
                  </select>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className={labelCls}>Bemutatkozás (bio)</label>
                <textarea value={form.bio} onChange={e => setForm(prev => ({ ...prev, bio: e.target.value }))} rows={4} className={inputCls} placeholder="Pár mondat az állatról..." />
              </div>

              {/* Traits */}
              <div>
                <label className={labelCls}>Tulajdonságok</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {form.traits.map(t => (
                    <span key={t} className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium border border-teal-100">
                      {t}
                      <button onClick={() => removeTrait(t)} className="hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    list="trait-vocabulary"
                    value={newTrait}
                    onChange={e => setNewTrait(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTrait(); } }}
                    className={`${inputCls} max-w-xs`}
                    placeholder="Új tulajdonság..."
                  />
                  <datalist id="trait-vocabulary">
                    {traitVocabulary.map(t => <option key={t} value={t} />)}
                  </datalist>
                  <button onClick={addTrait} disabled={addingTrait} className="px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-semibold hover:bg-teal-100 border border-teal-200 transition-colors flex items-center gap-1 disabled:opacity-50">
                    {addingTrait ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Hozzáad
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-400">
                  A rokon értelmű szavakat a már használt kulcsszóra cseréljük (pl. „kedves" → „Barátságos"), hogy a keresésben egy helyre kerüljenek.
                </p>
              </div>

              {/* ── Megtalálás helye ────────────────────────────────────
                  A szerkesztése ide került: az adatlap olvasó nézete fülekre
                  bomlott, a szerkesztés viszont egyben tartozik össze. */}
              <div>
                <label className={labelCls}>Megtalálás helye</label>
                <AddressAutocompleteInput
                  value={form.foundAddress}
                  onChange={address => setForm(prev => ({ ...prev, foundAddress: address }))}
                  onPlaceSelected={({ address, lat, lng }) => setForm(prev => ({ ...prev, foundAddress: address, foundLat: lat.toString(), foundLng: lng.toString() }))}
                  className={inputCls}
                />
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className={labelCls}>GPS szélesség</label>
                    <input type="number" step="0.0001" value={form.foundLat} onChange={e => setForm(prev => ({ ...prev, foundLat: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>GPS hosszúság</label>
                    <input type="number" step="0.0001" value={form.foundLng} onChange={e => setForm(prev => ({ ...prev, foundLng: e.target.value }))} className={inputCls} />
                  </div>
                </div>
                {/* Kézzel írt koordinátánál könnyű elvéteni egy tizedesjegyet;
                    a térkép ezt azonnal megmutatja. */}
                <StaticMap
                  lat={form.foundLat ? parseFloat(form.foundLat) : null}
                  lng={form.foundLng ? parseFloat(form.foundLng) : null}
                  label={form.foundAddress || null}
                  className="mt-3"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ═══ ÁTTEKINTÉS ═══════════════════════════════════════════════ */}
      {!isEditing && tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-3">Bemutatkozás</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{animal.bio || 'Nincs leírás.'}</p>
              {(animal.traits || []).length > 0 && (
                <>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-5 mb-2">Jellemzők</h3>
                  <div className="flex flex-wrap gap-2">
                    {(animal.traits || []).map((t, i) => (
                      <span key={i} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium">{t}</span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* A galéria a nézetben is kezelhető: a képműveletek amúgy is
                azonnaliak, nem az űrlap mentésének részei. */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-teal-600" />Galéria
              </h2>
              {canEdit ? (
                <AnimalPhotoManager
                  animalId={animalId!}
                  gallery={animal.gallery || []}
                  privateGallery={animal.privateGallery || []}
                  onChanged={refetch}
                />
              ) : (
                <ReadOnlyGallery gallery={animal.gallery || []} name={animal.name} />
              )}
            </div>

            {/* ─── Utókövetési helyzetjelentések ─────────────────────────
          Az Utókövetés fülön egy régebbi örökbefogadás kicsúszik a
          látókörből; a jelentések viszont az állathoz tartoznak, itt is meg
          kell találni őket. A szekció magától elmarad, ha nincs jelentés. */}
      {animalId && (
        <AnimalCheckInSection
          animalId={animalId}
          animalName={animal.name}
          adopterName={animal.adopterName}
        />
      )}

            {/* ── Bekerülési részletek ─────────────────────────────────
                Nem külön képernyő többé: itt, az áttekintés alatt szerkeszthető.
                A saját mentés gombja megmarad — az adatkör külön táblában él
                (animal_intake_records), és a profil űrlapjától függetlenül
                menthető. */}
            {canEdit && <AnimalIntakeDetailsPage embedded />}

            {/* ─── Megtalálás adatai ───────────────────────────────────
                Szerkesztése a szerkesztő űrlapon van; itt csak megjelenik. */}
            {animal.foundLocation && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Megtalálás adatai</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-gray-500">Típus</p><p className="font-medium">{INTAKE_LABELS[animal.intakeType || ''] || animal.intakeType || '—'}</p></div>
                  <div><p className="text-gray-500">Dátum</p><p className="font-medium">{animal.foundLocation.foundDate || '—'}</p></div>
                  {animal.foundLocation.address && (
                    <div className="col-span-2"><p className="text-gray-500">Cím</p><p className="font-medium">{animal.foundLocation.address}</p></div>
                  )}
                  <div className="col-span-2"><p className="text-gray-500">GPS</p><p className="font-mono">{animal.foundLocation.lat.toFixed(5)}, {animal.foundLocation.lng.toFixed(5)}</p></div>
                  <div className="col-span-2">
                    <StaticMap
                      lat={animal.foundLocation.lat}
                      lng={animal.foundLocation.lng}
                      label={animal.foundLocation.address || null}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Gyors Infó ─────────────────────────────────────────────
              A leggyakrabban keresett adatok egy helyen, a design szerint
              a jobb oldali oszlopban. */}
          <div className="space-y-5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-bold text-gray-800 mb-4">Gyors Infó</h2>
              <dl className="space-y-3 text-sm">
                <QuickFact label="Bekerülés dátuma" value={animal.intakeDate} />
                <QuickFact label="Bekerülés módja" value={INTAKE_LABELS[animal.intakeType || ''] || animal.intakeType} />
                <QuickFact label="Ivar" value={animal.gender} />
                <QuickFact label="Születési dátum" value={animal.birthDate} />
                <QuickFact label="Súly" value={animal.weightKg ? `${animal.weightKg} kg` : null} />
                <QuickFact label="Szín" value={animal.color} />
                {animal.adoptionDate && <QuickFact label="Örökbe adva" value={animal.adoptionDate} />}
                {animal.adopterName && <QuickFact label="Gazdi" value={animal.adopterName} />}
              </dl>
            </div>

            {/* ── Pénzügyi kampány ──────────────────────────────────────
                A korábbi három továbbvezető kártya (örökbefogadói igények,
                bekerülési részletek, egészségügyi adatok) tartalma átkerült a
                fülekre, így itt csak ez az egy marad. */}
            {canEdit && (
              // Helykitöltő: kampányt egyelőre semmilyen felületen nem lehet
              // létrehozni (a Pénzügyek oldal csak listázza őket). Szándékosan
              // nem visz sehová — egy kattintható kártya, ami egy olyan
              // oldalra dobna, ahol a művelet nincs meg, rosszabb a semminél.
              <div className="bg-white rounded-xl shadow-sm border border-dashed border-gray-200 p-5 flex items-start gap-3">
                <div className="p-2 bg-gray-50 rounded-lg shrink-0"><Wallet className="w-5 h-5 text-gray-400" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-500">Pénzügyi kampány létrehozása</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Gyűjtés indítása {animal.name} kezeléséhez — hamarosan
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ EGÉSZSÉGÜGY ═════════════════════════════════════════════ */}
      {!isEditing && tab === 'health' && (
        <div className="space-y-5">
          <AnimalHealthTab animal={animal} />
          {/* Az új bejegyzés rögzítése ide tartozik — eddig külön képernyőn volt. */}
          {canEdit && <AnimalMedicalPage embedded />}
        </div>
      )}

      {!isEditing && tab === 'requirements' && (
        <AnimalRequirementsPage embedded />
      )}

      {/* ═══ PÉNZÜGYEK ═══════════════════════════════════════════════ */}
      {!isEditing && tab === 'finance' && (
        <AnimalFinanceTab animalId={animalId} adoptionFee={animal.adoptionFee ?? null} />
      )}

      {/* ═══ ÉRDEKLŐDŐK ══════════════════════════════════════════════ */}
      {!isEditing && tab === 'leads' && (
        <div className="space-y-5">
      {/* ─── Leads — CRM Pipeline ──────────────────────────────────────── */}
      {animal.leads && animal.leads.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><HeartHandshake className="w-5 h-5 text-teal-600" />Érdeklődők — Örökbefogadási igények</h2>
          <div className="space-y-3">
            {animal.leads.map(lead => {
              const nextStatuses = STATUS_FLOW[lead.status] || [];
              return (
              <div key={lead.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold">{lead.name}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-700'}`}>{STATUS_LABELS[lead.status] || lead.status}</span>
                  </div>
                  <p className="text-sm text-gray-500">{lead.email}{lead.phone ? ` · ${lead.phone}` : ''}</p>
                  {lead.message && <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" />{lead.message}</p>}
                </div>
                {nextStatuses.length > 0 && (
                  <div className="flex items-center gap-2">
                    {updatingLead === lead.id ? (
                      <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                    ) : (
                      nextStatuses.map(ns => (
                        <button
                          key={ns}
                          onClick={() => handleLeadStatusChange(lead.id, ns)}
                          className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 ${ns === 'Rejected' ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'}`}
                        >
                          {ns === 'Rejected' ? <XCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                          {STATUS_LABELS[ns] || ns}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )})}
          </div>
          {/* Pipeline visualization */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-3 font-medium">Pipeline</p>
            <div className="flex items-center gap-1">
              {['New', 'Contacted', 'Vetting', 'Approved'].map((stage, i) => {
                const count = animal.leads?.filter(l => l.status === stage).length || 0;
                return (
                  <React.Fragment key={stage}>
                    <div className={`flex-1 text-center py-2 rounded-lg text-xs font-bold ${count > 0 ? (stage === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-teal-50 text-teal-700') : 'bg-gray-50 text-gray-400'}`}>
                      <div className="text-lg">{count}</div>
                      {STATUS_LABELS[stage]}
                    </div>
                    {i < 3 && <div className="text-gray-300 text-xs">→</div>}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Empty leads state */}
      {(!animal.leads || animal.leads.length === 0) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
          <HeartHandshake className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Még nincs örökbefogadási igény</p>
          <p className="text-sm text-gray-400">Az érdeklődők itt fognak megjelenni, amint valaki jelentkezik az állatra.</p>
        </div>
      )}
        </div>
      )}

      <StatusChangeDialog
        isOpen={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        animalName={animal.name}
        currentStatus={animal.status}
        onConfirm={handleChangeStatus}
      />
    </div>
  );
};

// ─── Gyors Infó sor ─────────────────────────────────────────────────────────

/** Egy címke/érték pár. Hiányzó érték helyén gondolatjel — a sor nem tűnik el,
 *  mert a hiány maga is információ ("ezt még nem tudjuk"). */
const QuickFact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <dt className="text-gray-500">{label}</dt>
    <dd className="font-medium text-gray-800 text-right">{value || <span className="text-gray-300">—</span>}</dd>
  </div>
);

// ─── Galéria olvasó nézetben ────────────────────────────────────────────────

/**
 * Csak a PUBLIKUS képek — ez a nézet azoknak jut, akiknek nincs szerkesztési
 * joguk ehhez a menhelyhez, tehát a privát képeket sem láthatják.
 */
const ReadOnlyGallery: React.FC<{ gallery: string[]; name: string }> = ({ gallery, name }) => {
  if (gallery.length === 0) {
    return <p className="text-sm text-gray-400">Nincs publikus kép.</p>;
  }
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
      {gallery.map((url, i) => (
        <div key={url} className="aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
          <img src={url} alt={`${name} — ${i + 1}. kép`} className="w-full h-full object-cover" />
        </div>
      ))}
    </div>
  );
};

// ─── Egészségügy fül ────────────────────────────────────────────────────────

const TRI_STATE_META: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  yes: { label: 'Megvan', className: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  no: { label: 'Nincs meg', className: 'bg-red-50 text-red-700 border-red-200', icon: <XCircle className="w-3.5 h-3.5" /> },
  unknown: { label: 'Nem tudjuk', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: <HelpCircle className="w-3.5 h-3.5" /> },
  missing: { label: 'Nincs adat', className: 'bg-gray-50 text-gray-400 border-gray-200', icon: <MinusCircle className="w-3.5 h-3.5" /> },
};

/**
 * Az állat egészségügyi állapotának olvasó nézete.
 *
 * A "nincs adat" (még rá sem kérdeztünk) és a "nem tudjuk" (megkérdeztük,
 * nem derült ki) szándékosan KÜLÖN jelenik meg: az első teendő, a második
 * lezárt kérdés. A részletes szerkesztés az Egészségügyi adatok oldalon van,
 * ide csak az összkép kerül.
 */
const AnimalHealthTab: React.FC<{ animal: Animal }> = ({ animal }) => {
  const vaccines = vaccinationsForSpecies(animal.species);
  const heartworm = animal.heartwormTest as HeartwormResult | null | undefined;

  const flags: Array<{ label: string; value: boolean | null | undefined }> = [
    { label: 'Oltott', value: animal.isVaccinated },
    { label: 'Ivartalanított', value: animal.isNeutered },
    { label: 'Parazitamentes', value: animal.isDewormed },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Syringe className="w-5 h-5 text-teal-600" />Oltások
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {vaccines.map(v => {
              const state = animal.vaccinations?.[v.key] ?? 'missing';
              const meta = TRI_STATE_META[state] || TRI_STATE_META.missing;
              return (
                <div key={v.key} className="flex items-center justify-between gap-2 py-1.5">
                  <span className="text-sm text-gray-700 truncate" title={v.hint}>{v.label}</span>
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.className}`}>
                    {meta.icon}{meta.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <div className="space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-bold text-gray-800 mb-4">Összefoglaló</h2>
          <dl className="space-y-3 text-sm">
            {flags.map(f => (
              <QuickFact
                key={f.label}
                label={f.label}
                value={f.value === true ? 'Igen' : f.value === false ? 'Nem' : null}
              />
            ))}
          </dl>
        </div>

        <div className={`rounded-xl shadow-sm border p-6 ${heartworm === 'positive' ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
          <h2 className="text-base font-bold text-gray-800 mb-2 flex items-center gap-2">
            {heartworm === 'positive' && <AlertTriangle className="w-4 h-4 text-red-600" />}
            Szívféreg-teszt
          </h2>
          {heartworm ? (
            <>
              <p className={`text-sm font-semibold ${heartworm === 'positive' ? 'text-red-800' : 'text-gray-800'}`}>
                {HEARTWORM_LABELS[heartworm]}
              </p>
              {animal.heartwormTestedAt && <p className="text-xs text-gray-500 mt-0.5">{animal.heartwormTestedAt}</p>}
              {heartworm === 'positive' && (
                <p className="text-xs text-red-700 mt-2">
                  Kezelést igényel, és befolyásolja az örökbeadhatóságát.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400">Nem volt teszt.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Pénzügyek fül ──────────────────────────────────────────────────────────

/**
 * Az állathoz köthető pénzmozgások.
 *
 * Az összeg előjele a `type` mezőből jön (bevétel/kiadás), nem az `amount`
 * előjeléből: az adatbázisban minden összeg pozitív, és a kettő összekeverése
 * a menhely egyenlegét mutatná fordítva.
 */
const AnimalFinanceTab: React.FC<{ animalId?: string; adoptionFee: number | null }> = ({ animalId, adoptionFee }) => {
  const { transactions, loading } = useAnimalTransactions(animalId);

  const income = transactions.filter(t => t.type === 'Income').reduce((sum, t) => sum + t.amount, 0);
  const expense = transactions.filter(t => t.type === 'Expense').reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-teal-600" />Pénzmozgások
        </h2>

        {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
        {!loading && transactions.length === 0 && (
          <p className="text-sm text-gray-400">Ehhez az állathoz még nem tartozik pénzügyi tétel.</p>
        )}
        {!loading && transactions.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {transactions.map(t => (
              <li key={t.id} className="py-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{t.description || t.category}</p>
                  <p className="text-xs text-gray-400">{t.category} · {t.date}</p>
                </div>
                <span className={`text-sm font-semibold flex-shrink-0 ${t.type === 'Income' ? 'text-green-700' : 'text-red-700'}`}>
                  {t.type === 'Income' ? '+' : '−'}{formatHUF(t.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-bold text-gray-800 mb-4">Összesítés</h2>
          <dl className="space-y-3 text-sm">
            <QuickFact label="Örökbefogadási díj" value={adoptionFee != null ? formatHUF(adoptionFee) : null} />
            <QuickFact label="Bevétel" value={<span className="text-green-700">{formatHUF(income)}</span>} />
            <QuickFact label="Kiadás" value={<span className="text-red-700">{formatHUF(expense)}</span>} />
            <div className="pt-3 border-t border-gray-100">
              <QuickFact
                label="Egyenleg"
                value={<span className={income - expense >= 0 ? 'text-green-700' : 'text-red-700'}>{formatHUF(income - expense)}</span>}
              />
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default AnimalProfilePage;
